import {ApolloError, ApolloQueryResult} from 'apollo-client';
import gql from 'graphql-tag';

import * as Generated from '../../generated/graphql';
import {ReportPageQuery} from '../../generated/graphql';
import {AccessOptions} from '../../util/permissions';

export const REPORT_PAGE_QUERY = gql`
  query ReportPage($projectName: String!, $entityName: String!) {
    project(name: $projectName, entityName: $entityName) {
      id
      access
      readOnly
      entity {
        id
        name
        defaultAccess
        readOnlyAdmin
        privateOnly
        isTeam
      }
    }
  }
`;

export type ReportPageQueryData = {
  project: {
    id: string;
    access: AccessOptions;
    readOnly: boolean;
    entity: {
      id: string;
      name: string;
      defaultAccess: AccessOptions;
      readOnlyAdmin: boolean;
      privateOnly: boolean;
      isTeam: boolean;
    };
  };
};

export const DEFAULT_REPORT_PAGE_VALUES: ReportPageQueryData = {
  project: {
    id: '',
    access: 'PRIVATE', // Default to most strict access
    readOnly: true,
    entity: {
      id: '',
      name: '',
      defaultAccess: 'PRIVATE', // TODO - figure out the best way to utilize the AccessOptions type here
      readOnlyAdmin: false,
      privateOnly: true,
      isTeam: false,
    },
  },
};

export type ReportPageQueryResult = {
  loading: boolean;
  error?: ApolloError;
  data?: ReportPageQueryData;
  refetch?: () => Promise<ApolloQueryResult<ReportPageQuery>>;
};

type ReportPageQueryArgs = {
  variables: {
    entityName: string;
    projectName?: string;
  };
};
export function useReportPageQueryResult(
  queryArgs: ReportPageQueryArgs
): ReportPageQueryResult {
  const {entityName, projectName} = queryArgs.variables;
  const {loading, data, error, refetch} = Generated.useReportPageQuery({
    variables: {entityName, projectName: projectName ?? ''},
    skip: projectName == null, // Do not query when projectName is not specified in url - this means this is a gallery report (old path)
  });

  if (loading || error) {
    return {
      loading,
      error,
      data: undefined,
    };
  }

  if (
    data == null ||
    data?.project == null ||
    data.project.__typename !== 'Project'
  ) {
    return {
      loading: false,
      error,
      data: undefined,
    };
  }

  const project = data.project;
  const defaultProject = DEFAULT_REPORT_PAGE_VALUES.project;

  const entity = data.project.entity;
  const defaultEntity = DEFAULT_REPORT_PAGE_VALUES.project.entity;

  return {
    loading: false,
    error,
    refetch,
    data: {
      project: {
        id: project?.id ?? defaultProject.id,
        access: (project?.access as AccessOptions) ?? defaultProject.access,
        readOnly: project?.readOnly ?? defaultProject.readOnly,
        entity: {
          id: entity?.id ?? defaultEntity.id,
          name: entity?.name ?? defaultEntity.name,
          defaultAccess:
            (entity?.defaultAccess as AccessOptions) ??
            defaultEntity.defaultAccess,
          readOnlyAdmin: entity?.readOnlyAdmin ?? defaultEntity.readOnlyAdmin,
          privateOnly: entity?.privateOnly ?? defaultEntity.privateOnly,
          isTeam: entity?.isTeam ?? defaultEntity.isTeam,
        },
      },
    },
  };
}
